import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardTitle } from "@/components/ui/card";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { Field } from "@/components/ui/input";
import { QrCode } from "@/components/ui/qr-code";
import { Select } from "@/components/ui/select";
import {
  verifyAddressAttestation,
  type AttestationRefusal,
  type AttestationTrustRoot,
  type AttestedAddress,
  type SignatureVerifier,
} from "./attestation";
import { PAY_COPY } from "./copy";
import { requestAddress, type PayError } from "./pay-api";
import type { PayAddressView, PayNetworkView } from "./pay-types";
import { useIdempotency } from "./use-pay";

/**
 * The deposit address — and the only place in the application where one can be
 * rendered.
 *
 * ## The rule this component exists to keep
 *
 * An address is displayed **only** in the `verified` phase, and that phase is
 * reachable only through `verifyAddressAttestation`. The address text is not
 * threaded into any other branch: the refusal, loading and error branches are
 * given the *reason*, never the value, so there is no path on which a
 * substituted address could be rendered by accident. That is deliberate
 * structure, not style — the page spec's first bug signal is
 * `address_shown_without_attestation`, and a component that held the address in
 * scope while refusing to show it would make that a matter of remembering.
 *
 * ## Order of operations
 *
 *  1. The payer picks a certified network and asks for an address.
 *  2. The service answers with an address **and** an attestation.
 *  3. The gate verifies the attestation against keys pinned in the bundle.
 *  4. Only then is the address, its checksum and its QR rendered.
 *
 * The gate's own failure list is rendered verbatim as a short reason code, so a
 * refusal can be diagnosed without the page ever needing to explain *why* an
 * untrusted address is untrusted.
 */

type Phase =
  | { kind: "idle" }
  | { kind: "requesting" }
  | { kind: "verifying" }
  | { kind: "verified"; address: AttestedAddress }
  | { kind: "refused"; reason: AttestationRefusal }
  | { kind: "error"; error: PayError };

export function AddressPanel({
  token,
  networks,
  declarationReady,
  trustRoot,
  verifier,
}: {
  token: string;
  networks: PayNetworkView[];
  /** The service requires the declaration before it will issue an address. */
  declarationReady: boolean;
  /** Injectable for the tests; production passes nothing and uses the pin. */
  trustRoot?: AttestationTrustRoot;
  verifier?: SignatureVerifier;
}) {
  const [selected, setSelected] = useState<string>(networks[0]?.network ?? "");
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  /**
   * The address waiting on the gate, held with the network it was **requested**
   * for. The gate binds the attestation to that value, not to whatever the
   * response claims, so a response cannot move the address to another chain by
   * relabelling itself.
   */
  const [pendingAddress, setPendingAddress] = useState<{ view: PayAddressView; requestedNetwork: string } | null>(null);
  const { keyFor, settle } = useIdempotency("pay-address");
  const network = networks.find((n) => n.network === selected) ?? null;

  // Verify outside the request handler so the gate is a single, inspectable
  // effect: it runs on a *new* address and on nothing else. The address is
  // dropped from state the moment the gate refuses, so no later render can
  // resurrect it.
  useEffect(() => {
    if (!pendingAddress) return;
    const { view, requestedNetwork } = pendingAddress;
    let live = true;
    setPhase({ kind: "verifying" });
    void (async () => {
      // A response that names a different network than the one requested is
      // refused before the gate even runs.
      const verdict =
        view.network !== "" && view.network !== requestedNetwork
          ? ({ ok: false, reason: "BINDING_MISMATCH" } as const)
          : await verifyAddressAttestation({
              address: view.address,
              network: requestedNetwork,
              attestation: view.attestation,
              trustRoot,
              verifier,
            });
      if (!live) return;
      if (verdict.ok) {
        setPhase({ kind: "verified", address: verdict.address });
        return;
      }
      // The unverified address is discarded here, together with the state that
      // held it.
      setPendingAddress(null);
      setPhase({ kind: "refused", reason: verdict.reason });
    })();
    return () => {
      live = false;
    };
  }, [pendingAddress, trustRoot, verifier]);

  const onRequest = useCallback(async () => {
    if (!network) return;
    const body = { network: network.network };
    setPhase({ kind: "requesting" });
    setPendingAddress(null);

    const result = await requestAddress(token, network.network, { idempotencyKey: keyFor(body) });
    if (result.ok) {
      // A decided attempt: the address exists now, so a later request for a
      // *different* network must mint its own key.
      settle(true);
      setPendingAddress({ view: result.data, requestedNetwork: network.network });
      return;
    }
    settle(result.status !== 0);
    setPhase({ kind: "error", error: result.error });
  }, [keyFor, network, settle, token]);

  const onSelectNetwork = useCallback((value: string) => {
    setSelected(value);
    // Changing network invalidates whatever was on screen: an address verified
    // for one chain must never sit under a label for another.
    setPendingAddress(null);
    setPhase({ kind: "idle" });
  }, []);

  const busy = phase.kind === "requesting" || phase.kind === "verifying";

  return (
    <Card>
      <CardBody>
        <CardTitle>{PAY_COPY.networkHeading}</CardTitle>
        <p className="mt-2 text-sm text-muted">{PAY_COPY.networkIntro}</p>

        {networks.length === 0 ? (
          <p className="mt-3 text-sm text-muted">
            This invoice has no network the issuer has certified for payment.
          </p>
        ) : (
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <Field label={PAY_COPY.networkLabel} htmlFor="pay-network">
              <Select
                id="pay-network"
                value={selected}
                disabled={busy}
                onChange={(event) => onSelectNetwork(event.target.value)}
              >
                {networks.map((option) => (
                  <option key={option.network} value={option.network}>
                    {option.label} · {option.asset.symbol}
                  </option>
                ))}
              </Select>
            </Field>
            <Button onClick={() => void onRequest()} disabled={busy || !declarationReady || !network}>
              {busy ? PAY_COPY.networkRequesting : PAY_COPY.networkRequestAddress}
            </Button>
          </div>
        )}

        {!declarationReady && networks.length > 0 && (
          <p className="mt-2 text-xs text-muted">{PAY_COPY.declarationRequiredNote}</p>
        )}

        {phase.kind === "error" && (
          <div role="alert" className="mt-3 rounded-md border border-danger-border bg-danger-soft px-3 py-2 text-sm text-danger">
            {phase.error.reasonCode}
            <SupportDetails reference={phase.error.supportReference} capability={phase.error.capability} />
          </div>
        )}

        {phase.kind === "refused" && <AddressRefused reason={phase.reason} />}

        {phase.kind === "verified" && network && (
          <AttestedAddressCard address={phase.address} network={network} />
        )}
      </CardBody>
    </Card>
  );
}

/**
 * The refusal. It renders the contract's sentence, the gate's reason code and
 * the anti-phishing line — and it has no access to the address, so there is
 * nothing here that could leak one.
 */
function AddressRefused({ reason }: { reason: AttestationRefusal }) {
  return (
    <div role="alert" className="mt-3 rounded-md border border-danger-border bg-danger-soft px-4 py-3">
      <p className="text-sm font-bold text-danger">{PAY_COPY.addressRefusedTitle}</p>
      <p className="mt-1 text-sm text-danger">{PAY_COPY.addressRefusedBody}</p>
      <p className="mt-2 font-mono text-xs text-danger">
        {PAY_COPY.addressRefusedCodeLabel}: {reason}
      </p>
    </div>
  );
}

/** The one branch in the application that renders a deposit address. */
function AttestedAddressCard({ address, network }: { address: AttestedAddress; network: PayNetworkView }) {
  return (
    <div className="mt-4 flex flex-col gap-4 rounded-md border border-line bg-surface-muted p-4 sm:flex-row sm:items-start">
      <QrCode value={address.address} label={PAY_COPY.addressQrLabel} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-ink">{PAY_COPY.addressHeading}</p>
        <dl className="mt-2 flex flex-col gap-2 text-sm">
          <div>
            <dt className="text-muted">{PAY_COPY.addressNetworkLabel}</dt>
            <dd className="font-semibold text-ink">
              {network.label} · {network.asset.symbol}
            </dd>
          </div>
          <div>
            <dt className="text-muted">{PAY_COPY.addressValueLabel}</dt>
            {/* Selectable text, not a link: nothing on this page may navigate
                away with the token in the URL. */}
            <dd className="break-all font-mono text-ink">{address.address}</dd>
          </div>
          <div>
            <dt className="text-muted">{PAY_COPY.addressChecksumLabel}</dt>
            <dd className="font-mono font-semibold text-ink">{address.checksum}</dd>
            <p className="mt-0.5 text-xs text-muted">{PAY_COPY.addressChecksumHint}</p>
          </div>
        </dl>
        <p className="mt-3 text-xs text-muted">{PAY_COPY.addressUnpaidNote}</p>
        <p className="mt-2 text-xs font-semibold text-warning">{PAY_COPY.addressAntiPhishing}</p>
      </div>
    </div>
  );
}
